import React, { useState, useEffect } from 'react';
import { message, Button, Spin, Divider, Tabs } from 'antd';
import { CheckOutlined, PlusOutlined, DeleteOutlined, HolderOutlined } from '@ant-design/icons';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  ApiConfigurationRule,
  EditFormData,
  UrlRule,
  ResponseRule,
  ConfigurationFilter,
  NestedRuleGroup,
  NestedResponseGroup,
} from './types';
import { transformFormDataToApi, transformApiToFormData } from './utils';
import { validateFormData } from './validation';
import {
  useGetIntelligentCollectionConfigurations,
  usePostIntelligentCollectionConfiguration,
  usePutIntelligentCollectionConfiguration,
  useDeleteIntelligentCollectionConfiguration,
  useReorderIntelligentCollectionConfigurations,
} from './queryHooks';
import {
  IntelligentCollectionHeader,
  NewConfigurationForm,
  ConfigurationRuleView,
  ConfigurationRuleEdit,
} from './components';
import './styles.css';

interface SortableRuleItemProps {
  id: string;
  disabled: boolean;
  children: React.ReactNode;
}

const SortableRuleItem: React.FC<SortableRuleItemProps> = ({ id, disabled, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({
    id,
    disabled,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: 'relative',
  };

  return (
    <div ref={setNodeRef} style={style}>
      {!disabled && (
        <HolderOutlined
          {...attributes}
          {...listeners}
          style={{
            cursor: 'grab',
            color: '#999',
            fontSize: 14,
            position: 'absolute',
            left: 4,
            top: 14,
            zIndex: 1,
          }}
        />
      )}
      {children}
    </div>
  );
};

const IntelligentCollection: React.FC = () => {
  const [selectedConfiguration, setSelectedConfiguration] =
    useState<ConfigurationFilter>('blocked-collected');
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [formData, setFormData] = useState<EditFormData>({
    allowBlock: 'collect',
    forAllUrl: false,
    urlRuleOperator: 'or',
    urlRules: [
      {
        id: Date.now().toString(),
        type: 'host',
        operator: 'starts with',
        values: [],
      },
    ],
    responseRuleOperator: 'and',
    responseRules: [],
  });

  const {
    data: configurations = [],
    isLoading,
    error,
    refetch,
  } = useGetIntelligentCollectionConfigurations();
  const { mutate: createConfiguration, isLoading: isCreating } =
    usePostIntelligentCollectionConfiguration();
  const { mutate: updateConfiguration, isLoading: isUpdating } =
    usePutIntelligentCollectionConfiguration();
  const { mutate: deleteConfiguration, isLoading: isDeleting } =
    useDeleteIntelligentCollectionConfiguration();
  const { mutate: reorderConfigurations, isLoading: isReordering } = useReorderIntelligentCollectionConfigurations();
  const [localConfigurations, setLocalConfigurations] = useState<ApiConfigurationRule[]>([]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  useEffect(() => {
    setLocalConfigurations(configurations);
  }, [configurations]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const ruleType = selectedConfiguration === 'collected' ? 'collect' : 'block';
    const typeItems = localConfigurations.filter((c) => c.rule === ruleType);
    const otherItems = localConfigurations.filter((c) => c.rule !== ruleType);

    const oldIndex = typeItems.findIndex((item) => item.id === active.id);
    const newIndex = typeItems.findIndex((item) => item.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = [...typeItems];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    const merged = ruleType === 'block'
      ? [...otherItems, ...reordered]
      : [...reordered, ...otherItems];
    setLocalConfigurations(merged);

    reorderConfigurations({ ruleType, orderedIds: reordered.map((item) => item.id) }, {
      onSuccess: () => {
        message.success('Order updated successfully');
      },
      onError: () => {
        message.error('Failed to reorder configurations');
        setLocalConfigurations(configurations);
      },
    });
  };

  const handleNewConfiguration = () => {
    if (editingRuleId !== null) {
      message.warning('Please finish editing the current configuration first.');
      return;
    }

    setEditingRuleId('new');
    setFormData({
      allowBlock: 'collect',
      forAllUrl: false,
      urlRuleOperator: 'or',
      urlRules: [
        {
          id: Date.now().toString(),
          type: 'host',
          operator: 'starts with',
          values: [],
        },
      ],
      responseRuleOperator: 'and',
      responseRules: [],
    });
  };

  const handleEditConfiguration = (rule: ApiConfigurationRule) => {
    if (editingRuleId !== null && editingRuleId !== rule.id) {
      message.warning('Please finish editing the current configuration first.');
      return;
    }

    setEditingRuleId(rule.id);
    setFormData(transformApiToFormData(rule));
  };

  const handleSaveConfiguration = () => {
    const validation = validateFormData(formData);

    if (!validation.isValid) {
      message.error('Please fill in all required fields before saving.');
      return;
    }

    if (editingRuleId === 'new') {
      const apiData = transformFormDataToApi(formData, '');
      const { id, ...newRulePayload } = apiData;

      createConfiguration(newRulePayload, {
        onSuccess: (data) => {
          message.success({
            content: (
              <div style={{ textAlign: 'left' }}>
                Configuration saved successfully.
                <br />
                Sensors may take up to 5 minutes to reflect changes.
              </div>
            ),
            duration: 5,
          });
          setEditingRuleId(null);
        },
        onError: (error) => {
          const errorMessage = error?.response?.data?.msg || 'Failed to save configuration';
          const errorData = error?.response?.data?.data || errorMessage;

          message.error(
            <div>
              <div>{errorData}</div>
            </div>,
          );
        },
      });
    } else {
      const ruleId = editingRuleId!;
      const apiData = transformFormDataToApi(formData, ruleId);

      updateConfiguration(apiData, {
        onSuccess: (data) => {
          message.success({
            content: (
              <div style={{ textAlign: 'left' }}>
                Configuration updated successfully.
                <br />
                Sensors may take up to 5 minutes to reflect changes.
              </div>
            ),
            duration: 5,
          });
          setEditingRuleId(null);
        },
        onError: (error) => {
          const errorMessage = error?.response?.data?.msg || 'Failed to update configuration';
          const errorData = error?.response?.data?.data || errorMessage;

          message.error(
            <div>
              <div>{errorData}</div>
            </div>,
          );
        },
      });
    }
  };

  const handleCancelEdit = () => {
    setEditingRuleId(null);
    setFormData({
      allowBlock: 'collect',
      urlRuleOperator: 'or',
      urlRules: [
        {
          id: Date.now().toString(),
          type: 'host',
          operator: 'starts with',
          values: [],
        },
      ],
      responseRuleOperator: 'and',
      responseRules: [],
    });
  };

  const findAndUpdateUrlGroup = (
    groups: (UrlRule | NestedRuleGroup)[],
    groupId: string,
    updateFn: (group: NestedRuleGroup) => NestedRuleGroup,
  ): (UrlRule | NestedRuleGroup)[] => {
    return groups.map((item) => {
      if ('rules' in item && item.id === groupId) {
        return updateFn(item);
      } else if ('rules' in item) {
        return {
          ...item,
          rules: findAndUpdateUrlGroup(item.rules, groupId, updateFn),
        };
      }
      return item;
    });
  };

  const findAndUpdateUrlRule = (
    groups: (UrlRule | NestedRuleGroup)[],
    groupId: string,
    ruleId: string,
    field: keyof UrlRule,
    value: UrlRule[keyof UrlRule],
  ): (UrlRule | NestedRuleGroup)[] => {
    return groups.map((item) => {
      if ('rules' in item) {
        if (item.id === groupId) {
          return {
            ...item,
            rules: item.rules.map((rule) =>
              !('rules' in rule) && rule.id === ruleId ? { ...rule, [field]: value } : rule,
            ),
          };
        } else {
          return {
            ...item,
            rules: findAndUpdateUrlRule(item.rules, groupId, ruleId, field, value),
          };
        }
      }
      return item;
    });
  };

  const addUrlRule = (groupId?: string) => {
    const newRule: UrlRule = {
      id: Date.now().toString(),
      type: 'host',
      operator: 'starts with',
      values: [],
    };

    if (!groupId || groupId === 'url-root') {
      setFormData((prev) => ({
        ...prev,
        urlRules: [...prev.urlRules, newRule],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        urlRules: findAndUpdateUrlGroup(prev.urlRules, groupId, (group) => ({
          ...group,
          rules: [...group.rules, newRule],
        })),
      }));
    }
  };

  const updateUrlRule = (groupId: string, ruleId: string, field: keyof UrlRule, value: UrlRule[keyof UrlRule]) => {
    if (groupId === 'url-root') {
      setFormData((prev) => ({
        ...prev,
        urlRules: prev.urlRules.map((rule) =>
          !('rules' in rule) && rule.id === ruleId ? { ...rule, [field]: value } : rule,
        ),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        urlRules: findAndUpdateUrlRule(prev.urlRules, groupId, ruleId, field, value),
      }));
    }
  };

  const removeUrlRule = (groupId: string, ruleId: string) => {
    if (groupId === 'url-root') {
      setFormData((prev) => ({
        ...prev,
        urlRules: prev.urlRules.filter((rule) => 'rules' in rule || rule.id !== ruleId),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        urlRules: findAndUpdateUrlGroup(prev.urlRules, groupId, (group) => ({
          ...group,
          rules: group.rules.filter((rule) => 'rules' in rule || rule.id !== ruleId),
        })),
      }));
    }
  };

  const addUrlGroup = (parentGroupId?: string) => {
    const newGroup: NestedRuleGroup = {
      id: Date.now().toString(),
      type: 'or',
      rules: [
        {
          id: (Date.now() + 1).toString(),
          type: 'host',
          operator: 'starts with',
          values: [],
        },
      ],
    };

    if (!parentGroupId || parentGroupId === 'url-root') {
      setFormData((prev) => ({
        ...prev,
        urlRules: [...prev.urlRules, newGroup],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        urlRules: findAndUpdateUrlGroup(prev.urlRules, parentGroupId, (group) => ({
          ...group,
          rules: [...group.rules, newGroup],
        })),
      }));
    }
  };

  const updateUrlGroup = (groupId: string, updates: Partial<NestedRuleGroup>) => {
    setFormData((prev) => ({
      ...prev,
      urlRules: findAndUpdateUrlGroup(prev.urlRules, groupId, (group) => ({
        ...group,
        ...updates,
      })),
    }));
  };

  const removeUrlGroup = (groupId: string) => {
    const removeFromGroups = (
      groups: (UrlRule | NestedRuleGroup)[],
    ): (UrlRule | NestedRuleGroup)[] => {
      return groups
        .filter((item) => {
          if ('rules' in item && item.id === groupId) {
            return false;
          }
          return true;
        })
        .map((item) => {
          if ('rules' in item) {
            return {
              ...item,
              rules: removeFromGroups(item.rules),
            };
          }
          return item;
        });
    };

    setFormData((prev) => ({
      ...prev,
      urlRules: removeFromGroups(prev.urlRules),
    }));
  };

  const findAndUpdateResponseGroup = (
    groups: (ResponseRule | NestedResponseGroup)[],
    groupId: string,
    updateFn: (group: NestedResponseGroup) => NestedResponseGroup,
  ): (ResponseRule | NestedResponseGroup)[] => {
    return groups.map((item) => {
      if ('rules' in item && item.id === groupId) {
        return updateFn(item);
      } else if ('rules' in item) {
        return {
          ...item,
          rules: findAndUpdateResponseGroup(item.rules, groupId, updateFn),
        };
      }
      return item;
    });
  };

  const findAndUpdateResponseRule = (
    groups: (ResponseRule | NestedResponseGroup)[],
    groupId: string,
    ruleId: string,
    field: keyof ResponseRule,
    value: ResponseRule[keyof ResponseRule],
  ): (ResponseRule | NestedResponseGroup)[] => {
    return groups.map((item) => {
      if ('rules' in item) {
        if (item.id === groupId) {
          return {
            ...item,
            rules: item.rules.map((rule) =>
              !('rules' in rule) && rule.id === ruleId ? { ...rule, [field]: value } : rule,
            ),
          };
        } else {
          return {
            ...item,
            rules: findAndUpdateResponseRule(item.rules, groupId, ruleId, field, value),
          };
        }
      }
      return item;
    });
  };

  const addResponseRule = (groupId?: string) => {
    const newRule: ResponseRule = {
      id: Date.now().toString(),
      type: 'duration',
      operator: 'greater than',
      value: '',
    };

    if (!groupId || groupId === 'response-root') {
      setFormData((prev) => ({
        ...prev,
        responseRules: [...prev.responseRules, newRule],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        responseRules: findAndUpdateResponseGroup(prev.responseRules, groupId, (group) => ({
          ...group,
          rules: [...group.rules, newRule],
        })),
      }));
    }
  };

  const updateResponseRule = (
    groupId: string,
    ruleId: string,
    field: keyof ResponseRule,
    value: ResponseRule[keyof ResponseRule],
  ) => {
    if (groupId === 'response-root') {
      setFormData((prev) => ({
        ...prev,
        responseRules: prev.responseRules.map((rule) =>
          !('rules' in rule) && rule.id === ruleId ? { ...rule, [field]: value } : rule,
        ),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        responseRules: findAndUpdateResponseRule(prev.responseRules, groupId, ruleId, field, value),
      }));
    }
  };

  const removeResponseRule = (groupId: string, ruleId: string) => {
    if (groupId === 'response-root') {
      setFormData((prev) => ({
        ...prev,
        responseRules: prev.responseRules.filter((rule) => 'rules' in rule || rule.id !== ruleId),
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        responseRules: findAndUpdateResponseGroup(prev.responseRules, groupId, (group) => ({
          ...group,
          rules: group.rules.filter((rule) => 'rules' in rule || rule.id !== ruleId),
        })),
      }));
    }
  };

  const addResponseGroup = (parentGroupId?: string) => {
    const newGroup: NestedResponseGroup = {
      id: Date.now().toString(),
      type: 'or',
      rules: [
        {
          id: (Date.now() + 1).toString(),
          type: 'duration',
          operator: 'greater than',
          value: '',
        },
      ],
    };

    if (!parentGroupId || parentGroupId === 'response-root') {
      setFormData((prev) => ({
        ...prev,
        responseRules: [...prev.responseRules, newGroup],
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        responseRules: findAndUpdateResponseGroup(prev.responseRules, parentGroupId, (group) => ({
          ...group,
          rules: [...group.rules, newGroup],
        })),
      }));
    }
  };

  const updateResponseGroup = (groupId: string, updates: Partial<NestedResponseGroup>) => {
    setFormData((prev) => ({
      ...prev,
      responseRules: findAndUpdateResponseGroup(prev.responseRules, groupId, (group) => ({
        ...group,
        ...updates,
      })),
    }));
  };

  const removeResponseGroup = (groupId: string) => {
    const removeFromGroups = (
      groups: (ResponseRule | NestedResponseGroup)[],
    ): (ResponseRule | NestedResponseGroup)[] => {
      return groups
        .filter((item) => {
          if ('rules' in item && item.id === groupId) {
            return false;
          }
          return true;
        })
        .map((item) => {
          if ('rules' in item) {
            return {
              ...item,
              rules: removeFromGroups(item.rules),
            };
          }
          return item;
        });
    };

    setFormData((prev) => ({
      ...prev,
      responseRules: removeFromGroups(prev.responseRules),
    }));
  };

  const handleDeleteConfiguration = (ruleId: string) => {
    deleteConfiguration(ruleId, {
      onSuccess: () => {
        message.success({
          content: (
            <div style={{ textAlign: 'left' }}>
              Configuration deleted successfully.
              <br />
              Sensors may take up to 5 minutes to reflect changes.
            </div>
          ),
          duration: 5,
        });
      },
      onError: (error) => {
        message.error('Failed to delete configuration');
      },
    });
  };

  const renderConfigurationRule = (rule: ApiConfigurationRule, index: number) => {
    const isEditing = editingRuleId === rule.id;

    if (isEditing) {
      return (
        <div key={rule.id}>
          <div
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.06)',
              padding: '12px',
              marginBottom: 16,
            }}
          >
            <span style={{ fontSize: 14, fontWeight: 500 }}>Edit Configuration</span>
          </div>

          <div
            style={{
              marginBottom: 16,
              borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
              paddingBottom: 16,
            }}
          >
            <ConfigurationRuleEdit
              formData={formData}
              onFormDataChange={setFormData}
              onAddUrlRule={addUrlRule}
              onUpdateUrlRule={updateUrlRule}
              onRemoveUrlRule={removeUrlRule}
              onAddUrlGroup={addUrlGroup}
              onUpdateUrlGroup={updateUrlGroup}
              onRemoveUrlGroup={removeUrlGroup}
              onAddResponseRule={addResponseRule}
              onUpdateResponseRule={updateResponseRule}
              onRemoveResponseRule={removeResponseRule}
              onAddResponseGroup={addResponseGroup}
              onUpdateResponseGroup={updateResponseGroup}
              onRemoveResponseGroup={removeResponseGroup}
            />
          </div>
        </div>
      );
    }

    return (
      <SortableRuleItem key={rule.id} id={rule.id} disabled={editingRuleId !== null || selectedConfiguration === 'blocked-collected'}>
        <ConfigurationRuleView
          rule={rule}
          index={index}
          onEdit={handleEditConfiguration}
          onDelete={handleDeleteConfiguration}
          isUpdating={isUpdating}
          isDeleting={isDeleting}
        />
      </SortableRuleItem>
    );
  };

  const renderNewConfiguration = () => {
    if (editingRuleId !== 'new') return null;

    return (
      <NewConfigurationForm
        formData={formData}
        onFormDataChange={setFormData}
        onAddUrlRule={addUrlRule}
        onUpdateUrlRule={updateUrlRule}
        onRemoveUrlRule={removeUrlRule}
        onAddUrlGroup={addUrlGroup}
        onUpdateUrlGroup={updateUrlGroup}
        onRemoveUrlGroup={removeUrlGroup}
        onAddResponseRule={addResponseRule}
        onUpdateResponseRule={updateResponseRule}
        onRemoveResponseRule={removeResponseRule}
        onAddResponseGroup={addResponseGroup}
        onUpdateResponseGroup={updateResponseGroup}
        onRemoveResponseGroup={removeResponseGroup}
      />
    );
  };

  if (error) {
    return <div>Error loading configurations: {(error as Error).message}</div>;
  }

  const filteredConfigurations = localConfigurations.filter((config) => {
    if (selectedConfiguration === 'blocked-collected') {
      return true;
    } else if (selectedConfiguration === 'blocked') {
      return config.rule === 'block';
    } else if (selectedConfiguration === 'collected') {
      return config.rule === 'collect';
    }
    return true;
  });

  const isSpinning = [isLoading, isCreating, isUpdating, isDeleting, isReordering].some(Boolean);

  return (
    <Spin spinning={isSpinning}>
      <div
        style={{
          maxHeight: 'calc(100vh - 200px)',
          overflowY: 'auto',
          paddingRight: '8px',
          paddingBottom: '100px',
        }}
      >
        <IntelligentCollectionHeader hasConfigurations={configurations.length > 0} />

        <div
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
            padding: '8px 16px',
            marginBottom: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 500 }}>Configurations</span>
          {editingRuleId !== 'new' && (
            <Button
              type="default"
              size="small"
              disabled={editingRuleId !== null}
              onClick={handleNewConfiguration}
              style={{
                color: editingRuleId !== null ? 'rgba(0, 0, 0, 0.25)' : 'rgba(0, 70, 255, 1)',
                fontSize: '13px',
                fontWeight: 400,
                borderColor:
                  editingRuleId !== null ? 'rgba(0, 0, 0, 0.15)' : 'rgba(179, 200, 255, 1)',
                display: 'inline-flex',
                alignItems: 'center',
              }}
              icon={<PlusOutlined />}
              data-test-id="add-new-configuration-button"
            >
              New
            </Button>
          )}
        </div>

        {renderNewConfiguration()}

        <div style={{ marginBottom: 16 }}>
          <Tabs
            activeKey={selectedConfiguration}
            onChange={(key) => setSelectedConfiguration(key as ConfigurationFilter)}
            size="small"
            data-test-id="configurations-tabs"
            items={[
              {
                key: 'blocked-collected',
                label: 'All',
              },
              {
                key: 'collected',
                label: 'Collect',
              },
              {
                key: 'blocked',
                label: 'Block',
              },
            ]}
          />
        </div>

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={filteredConfigurations.map((c) => c.id)} strategy={verticalListSortingStrategy}>
        <div data-test-id="configurations-list">
          {filteredConfigurations.map(renderConfigurationRule)}
        </div>
        </SortableContext>
        </DndContext>

        {editingRuleId !== null && (
          <div
            style={{
              position: 'fixed',
              left: '50%',
              bottom: 'calc(24px + env(safe-area-inset-bottom))',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              background: '#fff',
              padding: '14px 50px',
              borderRadius: 16,
              boxShadow: '0 8px 30px rgba(0, 0, 0, 0.25)',
              minWidth: 420,
              maxWidth: '90vw',
            }}
            aria-live="polite"
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
              }}
            >
              <span
                style={{
                  color: 'rgba(31, 31, 31, 1)',
                  fontWeight: 400,
                  fontSize: 14,
                }}
                data-test-id="unsavedChangesWarning"
              >
                Click save to apply changes
              </span>

              <div style={{ marginLeft: 'auto', display: 'flex', gap: 12 }}>
                <Button
                  type="default"
                  icon={<DeleteOutlined />}
                  onClick={handleCancelEdit}
                  style={{
                    color: 'rgba(255, 77, 79, 1)',
                    borderColor: 'rgba(255, 77, 79, 1)',
                  }}
                  data-test-id="discard-all-button"
                >
                  Discard All
                </Button>

                <Button
                  type="primary"
                  loading={isCreating || isUpdating}
                  disabled={isCreating || isUpdating}
                  icon={<CheckOutlined />}
                  onClick={handleSaveConfiguration}
                  data-test-id="save-button"
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Spin>
  );
};

export default IntelligentCollection;
